import type { Root, Element } from 'hast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

/**
 * Block-level elements we tag with a source line. These are the structural
 * blocks the outline panel + content-aware scroll sync map back to editor
 * positions; inline elements are intentionally excluded.
 */
const BLOCK_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'table',
  'hr',
  'div',
]);

/**
 * Rehype plugin that records source-range metadata on each block-level
 * element as data attributes:
 *
 *   data-line          — 1-based start line (outline jump, scroll sync)
 *   data-source-start  — character offset of the block's start in the
 *                        original markdown (Copy-as-markdown affordance)
 *   data-source-end    — character offset of the block's end (exclusive)
 *
 * Must run while the mdast→hast `position` data is still intact — i.e.
 * immediately after `remark-rehype` and BEFORE `rehype-raw`/`rehype-sanitize`
 * (raw re-parses, which can drop positions). `remark-rehype` copies the mdast
 * node's `position` onto the hast node.
 *
 * All three are harmless numeric attributes; the sanitize schema allow-lists
 * them on all elements (they carry no URL / script semantics).
 */
export const rehypeSourceLines: Plugin<[], Root> = () => {
  return (tree: Root) => {
    visit(tree, 'element', (node: Element) => {
      if (!BLOCK_TAGS.has(node.tagName)) return;
      const pos = node.position;
      const line = pos?.start?.line;
      if (typeof line !== 'number') return;
      node.properties = node.properties ?? {};
      node.properties.dataLine = line;
      const startOffset = pos?.start?.offset;
      const endOffset = pos?.end?.offset;
      if (typeof startOffset === 'number' && typeof endOffset === 'number') {
        node.properties.dataSourceStart = startOffset;
        node.properties.dataSourceEnd = endOffset;
      }
    });
  };
};
